import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useGroup } from "@/context/GroupContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CardSkeleton } from "@/components/Skeletons";
import { Shield, Trash2 } from "lucide-react";

export default function GroupDetails() {
  const { group, groupId, isAdmin, refresh, slug } = useGroup();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description || "");
    }
  }, [group]);

  if (!group) return <CardSkeleton />;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/groups/${groupId}`, { name, description });
      toast.success("Group updated");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${group.name}" and all its data? This cannot be undone.`)) return;
    try {
      await api.delete(`/groups/${groupId}`);
      toast.success("Group deleted");
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5 lg:max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Manage</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Group settings</h1>
      </div>

      {!isAdmin && (
        <div className="rounded-3xl bg-amber-50 border border-amber-200 p-5 flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-800">Read-only</div>
            <div className="text-sm text-amber-700">Only the admin can edit or delete this group.</div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl p-5 border border-slate-200 space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Name</Label>
          <Input disabled={!isAdmin} value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="group-name-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Group link</Label>
          <div className="mt-2 h-12 rounded-2xl bg-slate-50 border border-slate-200 px-4 flex items-center text-sm text-slate-500 font-mono">
            {window.location.host}/<b className="text-slate-900">{group.slug}</b>
          </div>
          <p className="text-xs text-slate-500 mt-1.5">Group slugs can't be changed once created.</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Description</Label>
          <Textarea disabled={!isAdmin} value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 rounded-2xl" data-testid="group-description-input" />
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button disabled={busy} onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="group-save-button">
              {busy ? "Saving…" : "Save changes"}
            </Button>
            <Button
              onClick={del}
              variant="outline"
              className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              data-testid="group-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete group
            </Button>
          </div>
        )}
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="heading text-lg font-semibold text-slate-900">Members ({group.members?.length || 0})</h2>
          {isAdmin && (
            <button
              onClick={() => navigate(`/${slug}/members`)}
              className="text-xs font-semibold text-slate-500 hover:text-slate-900"
            >
              Manage on People page →
            </button>
          )}
        </div>
        <div className="space-y-2">
          {group.members?.map((m) => (
            <div key={m.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-slate-200">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100">
                {m.photo && <img src={m.photo} alt={m.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-slate-900">{m.name}</div>
                  {group.admin_id === m.id && (
                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Admin</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{m.email}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
